export const translationCenterDe = {
  title: 'Übersetzungsübersicht',
  description:
    'Vollständigkeit prüfen, maschinelle Vorschläge freigeben und fehlende Texte gesammelt erzeugen.',
  export: 'Fehlerliste exportieren',
  resources: 'Felder und Kategorien',
  missing: 'Fehlende Texte',
  reviewRequired: 'Ungeprüfte Vorschläge',
  stale: 'Veraltete Übersetzungen',
  localeProgress: '{{complete}} von {{total}} vollständig',
  filters: 'Filter',
  missingOnly: 'Nur fehlende Übersetzungen',
  reviewOnly: 'Nur maschinell und ungeprüft',
  staleOnly: 'Nur veraltete Übersetzungen',
  staleBadge: '{{count}} veraltet',
  language: 'Sprache',
  entity: 'Entität',
  category: 'Kategorie',
  status: 'Status',
  targetLanguages: 'Zielsprachen',
  batchTitle: 'Fehlende Übersetzungen gesammelt erzeugen',
  batchHint:
    'Der Auftrag läuft begrenzt im Hintergrund. Ergebnisse bleiben ungeprüft und aktivieren keine Definition.',
  generateMissing: 'Fehlende erzeugen',
  batchStatuses: {
    QUEUED: 'Eingereiht',
    PROCESSING: 'In Bearbeitung',
    PAUSED: 'Pausiert',
    COMPLETED: 'Abgeschlossen',
    FAILED: 'Mit Fehlern beendet',
    CANCELLED: 'Abgebrochen',
  },
  batchProgress: 'Fortschritt des Übersetzungsauftrags',
  batchNumbers: '{{completed}} erfolgreich, {{failed}} fehlgeschlagen, {{total}} gesamt',
  reviewedCount: '{{count}} Übersetzungen wurden geprüft.',
  resultCount: '{{count}} Einträge',
  reviewSelected: '{{count}} ausgewählte prüfen',
  empty: 'Keine Einträge entsprechen den Filtern.',
  select: 'Auswahl',
  resource: 'Feld oder Kategorie',
  languages: 'Sprachstatus',
  actions: 'Aktionen',
  selectResource: '{{name}} auswählen',
  review: 'Prüfen',
  open: 'Öffnen',
  all: 'Alle',
  statuses: { DRAFT: 'Entwurf', IN_REVIEW: 'Zur Prüfung', ACTIVE: 'Aktiv', ARCHIVED: 'Archiviert' },
  kinds: { DEFINITION: 'Zusatzfeld', GROUP: 'Kategorie' },
} as const;

export const translationCenterEn = {
  title: 'Translation overview',
  description:
    'Check completeness, approve machine suggestions and generate missing text in batches.',
  export: 'Export issue list',
  resources: 'Fields and categories',
  missing: 'Missing text',
  reviewRequired: 'Unreviewed suggestions',
  stale: 'Outdated translations',
  localeProgress: '{{complete}} of {{total}} complete',
  filters: 'Filters',
  missingOnly: 'Missing translations only',
  reviewOnly: 'Machine-generated and unreviewed only',
  staleOnly: 'Outdated translations only',
  staleBadge: '{{count}} outdated',
  language: 'Language',
  entity: 'Entity',
  category: 'Category',
  status: 'Status',
  targetLanguages: 'Target languages',
  batchTitle: 'Generate missing translations in a batch',
  batchHint:
    'The job runs with limited background concurrency. Results remain unreviewed and never activate a definition.',
  generateMissing: 'Generate missing',
  batchStatuses: {
    QUEUED: 'Queued',
    PROCESSING: 'Processing',
    PAUSED: 'Paused',
    COMPLETED: 'Completed',
    FAILED: 'Completed with errors',
    CANCELLED: 'Cancelled',
  },
  batchProgress: 'Translation job progress',
  batchNumbers: '{{completed}} successful, {{failed}} failed, {{total}} total',
  reviewedCount: '{{count}} translations were reviewed.',
  resultCount: '{{count}} entries',
  reviewSelected: 'Review {{count}} selected',
  empty: 'No entries match the filters.',
  select: 'Select',
  resource: 'Field or category',
  languages: 'Language status',
  actions: 'Actions',
  selectResource: 'Select {{name}}',
  review: 'Review',
  open: 'Open',
  all: 'All',
  statuses: { DRAFT: 'Draft', IN_REVIEW: 'In review', ACTIVE: 'Active', ARCHIVED: 'Archived' },
  kinds: { DEFINITION: 'Custom field', GROUP: 'Category' },
} as const;

export const translationCenterFr = {
  title: 'Vue d’ensemble des traductions',
  description:
    'Contrôlez l’exhaustivité, validez les suggestions automatiques et générez les textes manquants par lot.',
  export: 'Exporter la liste des erreurs',
  resources: 'Champs et catégories',
  missing: 'Textes manquants',
  reviewRequired: 'Suggestions non contrôlées',
  stale: 'Traductions obsolètes',
  localeProgress: '{{complete}} sur {{total}} complets',
  filters: 'Filtres',
  missingOnly: 'Traductions manquantes uniquement',
  reviewOnly: 'Automatiques et non contrôlées uniquement',
  staleOnly: 'Traductions obsolètes uniquement',
  staleBadge: '{{count}} obsolète(s)',
  language: 'Langue',
  entity: 'Entité',
  category: 'Catégorie',
  status: 'Statut',
  targetLanguages: 'Langues cibles',
  batchTitle: 'Générer les traductions manquantes par lot',
  batchHint:
    'La tâche s’exécute en arrière-plan avec une concurrence limitée. Les résultats restent non contrôlés et n’activent aucune définition.',
  generateMissing: 'Générer les manquantes',
  batchStatuses: {
    QUEUED: 'En attente',
    PROCESSING: 'En cours',
    PAUSED: 'En pause',
    COMPLETED: 'Terminé',
    FAILED: 'Terminé avec des erreurs',
    CANCELLED: 'Annulé',
  },
  batchProgress: 'Progression de la tâche de traduction',
  batchNumbers: '{{completed}} réussies, {{failed}} en échec, {{total}} au total',
  reviewedCount: '{{count}} traductions ont été contrôlées.',
  resultCount: '{{count}} entrées',
  reviewSelected: 'Contrôler les {{count}} sélectionnées',
  empty: 'Aucune entrée ne correspond aux filtres.',
  select: 'Sélection',
  resource: 'Champ ou catégorie',
  languages: 'Statut linguistique',
  actions: 'Actions',
  selectResource: 'Sélectionner {{name}}',
  review: 'Contrôler',
  open: 'Ouvrir',
  all: 'Tous',
  statuses: { DRAFT: 'Brouillon', IN_REVIEW: 'À contrôler', ACTIVE: 'Actif', ARCHIVED: 'Archivé' },
  kinds: { DEFINITION: 'Champ personnalisé', GROUP: 'Catégorie' },
} as const;

export const translationCenterEs = {
  title: 'Resumen de traducciones',
  description:
    'Compruebe la integridad, apruebe sugerencias automáticas y genere por lotes los textos que faltan.',
  export: 'Exportar lista de errores',
  resources: 'Campos y categorías',
  missing: 'Textos que faltan',
  reviewRequired: 'Sugerencias sin revisar',
  stale: 'Traducciones obsoletas',
  localeProgress: '{{complete}} de {{total}} completos',
  filters: 'Filtros',
  missingOnly: 'Solo traducciones que faltan',
  reviewOnly: 'Solo automáticas y sin revisar',
  staleOnly: 'Solo traducciones obsoletas',
  staleBadge: '{{count}} obsoletas',
  language: 'Idioma',
  entity: 'Entidad',
  category: 'Categoría',
  status: 'Estado',
  targetLanguages: 'Idiomas de destino',
  batchTitle: 'Generar por lotes las traducciones que faltan',
  batchHint:
    'La tarea se ejecuta en segundo plano con concurrencia limitada. Los resultados quedan sin revisar y no activan ninguna definición.',
  generateMissing: 'Generar las que faltan',
  batchStatuses: {
    QUEUED: 'En cola',
    PROCESSING: 'En curso',
    PAUSED: 'En pausa',
    COMPLETED: 'Completada',
    FAILED: 'Completada con errores',
    CANCELLED: 'Cancelada',
  },
  batchProgress: 'Progreso de la tarea de traducción',
  batchNumbers: '{{completed}} correctas, {{failed}} fallidas, {{total}} en total',
  reviewedCount: 'Se revisaron {{count}} traducciones.',
  resultCount: '{{count}} entradas',
  reviewSelected: 'Revisar {{count}} seleccionadas',
  empty: 'Ninguna entrada coincide con los filtros.',
  select: 'Selección',
  resource: 'Campo o categoría',
  languages: 'Estado por idioma',
  actions: 'Acciones',
  selectResource: 'Seleccionar {{name}}',
  review: 'Revisar',
  open: 'Abrir',
  all: 'Todos',
  statuses: {
    DRAFT: 'Borrador',
    IN_REVIEW: 'En revisión',
    ACTIVE: 'Activo',
    ARCHIVED: 'Archivado',
  },
  kinds: { DEFINITION: 'Campo adicional', GROUP: 'Categoría' },
} as const;

export const translationCenterRu = {
  title: 'Обзор переводов',
  description:
    'Проверяйте полноту, подтверждайте машинные варианты и создавайте недостающие тексты пакетом.',
  export: 'Экспорт списка ошибок',
  resources: 'Поля и категории',
  missing: 'Недостающие тексты',
  reviewRequired: 'Непроверенные варианты',
  stale: 'Устаревшие переводы',
  localeProgress: 'заполнено {{complete}} из {{total}}',
  filters: 'Фильтры',
  missingOnly: 'Только недостающие переводы',
  reviewOnly: 'Только машинные без проверки',
  staleOnly: 'Только устаревшие переводы',
  staleBadge: 'устарело: {{count}}',
  language: 'Язык',
  entity: 'Сущность',
  category: 'Категория',
  status: 'Статус',
  targetLanguages: 'Целевые языки',
  batchTitle: 'Пакетное создание недостающих переводов',
  batchHint:
    'Задача работает в фоновом режиме с ограниченной параллельностью. Результаты не считаются проверенными и не активируют определения.',
  generateMissing: 'Создать недостающие',
  batchStatuses: {
    QUEUED: 'В очереди',
    PROCESSING: 'Выполняется',
    PAUSED: 'Приостановлено',
    COMPLETED: 'Завершено',
    FAILED: 'Завершено с ошибками',
    CANCELLED: 'Отменено',
  },
  batchProgress: 'Ход задачи перевода',
  batchNumbers: 'успешно: {{completed}}, с ошибкой: {{failed}}, всего: {{total}}',
  reviewedCount: 'Проверено переводов: {{count}}.',
  resultCount: 'Записей: {{count}}',
  reviewSelected: 'Проверить выбранные ({{count}})',
  empty: 'Нет записей, соответствующих фильтрам.',
  select: 'Выбор',
  resource: 'Поле или категория',
  languages: 'Статус языков',
  actions: 'Действия',
  selectResource: 'Выбрать {{name}}',
  review: 'Проверить',
  open: 'Открыть',
  all: 'Все',
  statuses: {
    DRAFT: 'Черновик',
    IN_REVIEW: 'На проверке',
    ACTIVE: 'Активно',
    ARCHIVED: 'В архиве',
  },
  kinds: { DEFINITION: 'Дополнительное поле', GROUP: 'Категория' },
} as const;

export const translationCenterTr = {
  title: 'Çeviri genel bakışı',
  description:
    'Eksiksizliği denetleyin, makine önerilerini onaylayın ve eksik metinleri toplu oluşturun.',
  export: 'Hata listesini dışa aktar',
  resources: 'Alanlar ve kategoriler',
  missing: 'Eksik metinler',
  reviewRequired: 'İncelenmemiş öneriler',
  stale: 'Güncelliğini yitirmiş çeviriler',
  localeProgress: '{{total}} kaydın {{complete}} tanesi eksiksiz',
  filters: 'Filtreler',
  missingOnly: 'Yalnızca eksik çeviriler',
  reviewOnly: 'Yalnızca makine çevirisi ve incelenmemiş',
  staleOnly: 'Yalnızca güncelliğini yitirmiş çeviriler',
  staleBadge: '{{count}} eski',
  language: 'Dil',
  entity: 'Varlık',
  category: 'Kategori',
  status: 'Durum',
  targetLanguages: 'Hedef diller',
  batchTitle: 'Eksik çevirileri toplu oluştur',
  batchHint:
    'İş, sınırlı eşzamanlılıkla arka planda çalışır. Sonuçlar incelenmemiş kalır ve hiçbir tanımı etkinleştirmez.',
  generateMissing: 'Eksikleri oluştur',
  batchStatuses: {
    QUEUED: 'Sırada',
    PROCESSING: 'İşleniyor',
    PAUSED: 'Duraklatıldı',
    COMPLETED: 'Tamamlandı',
    FAILED: 'Hatalarla tamamlandı',
    CANCELLED: 'İptal edildi',
  },
  batchProgress: 'Çeviri işi ilerlemesi',
  batchNumbers: '{{completed}} başarılı, {{failed}} başarısız, toplam {{total}}',
  reviewedCount: '{{count}} çeviri incelendi.',
  resultCount: '{{count}} kayıt',
  reviewSelected: 'Seçilen {{count}} kaydı incele',
  empty: 'Filtrelerle eşleşen kayıt yok.',
  select: 'Seçim',
  resource: 'Alan veya kategori',
  languages: 'Dil durumu',
  actions: 'İşlemler',
  selectResource: '{{name}} seç',
  review: 'İncele',
  open: 'Aç',
  all: 'Tümü',
  statuses: { DRAFT: 'Taslak', IN_REVIEW: 'İncelemede', ACTIVE: 'Etkin', ARCHIVED: 'Arşivlendi' },
  kinds: { DEFINITION: 'Ek alan', GROUP: 'Kategori' },
} as const;
